/**
 * SHER-638 (SHER-208/D) — canonical helpers to resolve the CAPTURED (frozen) section
 * title / field label a historical clinical record carries, so every surface that shows
 * a record — read view (show_records_section.jsx), PDF (SubmissionPDFDocument.tsx) and
 * the edit form (FillSurvey.tsx) — renders it identically, with the labels it had at
 * capture rather than the live (renamed) survey.
 *
 * The API freezes the labels onto each SurveyRecord (sectionNameSnapshot /
 * fieldLabelSnapshot) at capture; template renames mutate the live rows in place, which
 * historical records FK-reference, so without the snapshot they re-render with the new
 * titles — a medico-legal integrity break (Pedro, SHER-208 #25).
 *
 * All helpers return `undefined` when there is no snapshot (legacy records created before
 * this shipped). Every call site falls back to the live value with `|| liveValue`, so the
 * fallback rule is a single, predictable one across all three surfaces.
 */

interface RecordLike {
    surveyField?: { id?: any;[key: string]: any };
    fieldLabelSnapshot?: string | null;
    sectionNameSnapshot?: string | null;
    [key: string]: any;
}
interface SectionLike {
    fields?: { id?: any;[key: string]: any }[];
    [key: string]: any;
}
interface InitDataLike {
    surveyRecords?: RecordLike[];
    [key: string]: any;
}

// --- Record-level primitives -------------------------------------------------
// The read view and PDF receive records already grouped per section, so they resolve
// the captured labels straight off the record(s).

/** The captured field label of a single record, or `undefined` for a legacy record. */
export function capturedFieldLabelFromRecord(record: RecordLike | undefined | null): string | undefined {
    return record && record.fieldLabelSnapshot != null ? (record.fieldLabelSnapshot as string) : undefined;
}

/**
 * The captured section title shared by a group of records (all belonging to one section),
 * or `undefined` when none carries a snapshot (legacy). The first record with a snapshot
 * holds the captured title.
 */
export function capturedSectionNameFromRecords(records: RecordLike[] | undefined | null): string | undefined {
    if (!Array.isArray(records)) {
        return undefined;
    }
    const record = records.find((r) => r && r.sectionNameSnapshot != null);
    return record ? (record.sectionNameSnapshot as string) : undefined;
}

// --- Field-id-keyed variants -------------------------------------------------
// FillSurvey rebuilds the edit form from the LIVE survey structure, so it must locate
// the matching record by field identity (surveyField.id — the same key getArchivedFields
// uses, and the key a rename-in-place preserves).

/** The captured field label for `fieldId` from a submission's records, or `undefined`. */
export function capturedFieldLabel(fieldId: any, initData: InitDataLike | undefined | null): string | undefined {
    const records = initData?.surveyRecords;
    if (!Array.isArray(records) || fieldId == null) {
        return undefined;
    }
    const record = records.find(
        (r) => r && r.surveyField && r.surveyField.id === fieldId && r.fieldLabelSnapshot != null,
    );
    return capturedFieldLabelFromRecord(record);
}

/** The captured section title for a live `section` from a submission's records. */
export function capturedSectionName(section: SectionLike | undefined | null, initData: InitDataLike | undefined | null): string | undefined {
    const records = initData?.surveyRecords;
    if (!Array.isArray(records) || !section) {
        return undefined;
    }
    const fieldIds = new Set((section.fields || []).map((f) => f && f.id));
    const sectionRecords = records.filter((r) => r && r.surveyField && fieldIds.has(r.surveyField.id));
    return capturedSectionNameFromRecords(sectionRecords);
}
